import { useState } from "react";

import { useQuery, useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../../../../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../../../../context/StateContext";
import useActionSyncUser from "../../../../../../../../../../hooks/useSyncUser";
import { Company } from "../../../../../../../../../../models/company";
import { Currency } from "../../../../../../../../../../models/currency";
import { Measurement } from "../../../../../../../../../../models/measurement";
import CompanyService from "../../../../../../../../../../services/company.service";
import CurrencyService from "../../../../../../../../../../services/currency.service";
import ItemsService from "../../../../../../../../../../services/items.service";
import MeasurementService from "../../../../../../../../../../services/measurement.service";
import { Status, MeasurementType } from "../../../../../../../../../../util/enums";

const useActions = () => {
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const [company, setCompany] = useState<Company>();
  const [dimensionList, setDimensionList] = useState<Measurement[]>([]);
  const [currencyList, setCurrencyList] = useState<Currency[]>();
  const [weightList, setWeightList] = useState<Measurement[]>([]);
  const [hasDefaultValue, setIsDefaultValue] = useState(true);
  const [disableAutoHardwareBox, setDisableAutoHardwareBox] = useState<boolean>(true);
  const { mutate: syncUser } = useActionSyncUser();

  const { refetch, isFetching } = useQuery(
    ["company.list"],
    () =>
      CompanyService.getById(currentUser?.companyId || 0, {
        include: JSON.stringify([
          {
            association: "configs",
          },
        ]),
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          setCompany(response.data);
        }
      },
    }
  );

  const { isFetching: isFetchingCurrency } = useQuery(
    ["currency.list"],
    () =>
      CurrencyService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data?.length > 0) setCurrencyList(response.data);
      },
    }
  );

  const { isFetching: isFetchingDimensions } = useQuery(
    [`measurement.${MeasurementType.DIMENSION}.list`],
    () =>
      MeasurementService.getAll({
        status: Status.ACTIVE,
        type: MeasurementType.DIMENSION,
        companyId: currentUser?.companyId,
      }),
    {
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array) {
          setDimensionList(response.data);
        }
      },
    }
  );

  const { isFetching: isFetchingWeights } = useQuery(
    [`measurement.${MeasurementType.WEIGHT}.list`],
    () =>
      MeasurementService.getAll({
        status: Status.ACTIVE,
        type: MeasurementType.WEIGHT,
        companyId: currentUser?.companyId,
      }),
    {
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array) {
          setWeightList(response.data);
        }
      },
    }
  );

  const { isFetching: isFetchingAutoHardwareBox } = useQuery(
    ["items.list"],
    () =>
      ItemsService.count({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        isHardwareBox: 1,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setDisableAutoHardwareBox(response.data === 0);
      },
    }
  );

  const { mutate, isLoading: isUpdating } = useMutation(
    ["company.update"],
    ({ id, company }: { id: number; company: Company }) => CompanyService.update(id, company),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"), {
          id: "request.notification.update.company",
        });
        refetch();
        syncUser();
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
        setIsDefaultValue(true);
      },
    }
  );

  return {
    mutate,
    setIsDefaultValue,
    isLoading:
      isFetching ||
      isFetchingWeights ||
      isFetchingDimensions ||
      isFetchingCurrency ||
      isFetchingAutoHardwareBox,
    isUpdating,
    company,
    dimensionList,
    currencyList,
    weightList,
    hasDefaultValue,
    disableAutoHardwareBox,
    refetch,
  };
};

export default useActions;
